import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Siren, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { resolveMedia } from "../lib/media";
import CampaignNotice from "./CampaignNotice";
import CampaignHelpButtons from "./CampaignHelpButtons";
import RescueShareButtons from "./RescueShareButtons";

const OPEN = ["Needs Help", "Rescue Underway", "Partially Safe"];

// Featured campaign on the homepage. Public: newest published open alert.
// Admin preview (?campaign_preview=<id>) shows a draft to the signed-in admin only.
export default function HomeCampaignSection() {
  const [params] = useSearchParams();
  const [r, setR] = useState(null);
  const previewId = params.get("campaign_preview");

  useEffect(() => {
    const token = window.sessionStorage.getItem("paws_admin_token");
    const loadPublic = () =>
      api.get("/urgent-rescues").then(({ data }) => setR((data.items || []).find((x) => OPEN.includes(x.status)) || null)).catch(() => {});
    if (previewId && token) {
      api.get(`/admin/urgent-rescues/${previewId}`, { headers: { "X-Admin-Token": token } }).then(({ data }) => setR(data)).catch(loadPublic);
      return;
    }
    loadPublic();
  }, [previewId]);

  if (!r) return null;
  const draft = r.is_published === false;
  const flyer = r.photo_url || (r.share_image_approved !== false && r.share_image_url) || r.flyer_url;
  return (
    <section className="border-b-4 border-red-600 bg-white" data-testid="home-campaign-section">
      {draft && (
        <div className="bg-zinc-900 text-white text-sm px-6 py-2 text-center" data-testid="home-campaign-draft-banner">
          <b className="text-amber-400">DRAFT PREVIEW</b> — only you can see this section. It appears publicly after you click Publish.
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          {flyer && (
            <img src={resolveMedia(flyer)} alt={r.photo_caption || r.title} className="w-full max-h-[640px] object-contain bg-zinc-950 border-2 border-zinc-900" data-testid="home-campaign-flyer" />
          )}
        </div>
        <div className="lg:col-span-7 space-y-5">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest">
            <Siren size={13} className="animate-pulse" /> Urgent rescue campaign
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none" data-testid="home-campaign-title">{r.title}</h2>
          <p className="text-base text-zinc-700 leading-relaxed">
            {r.animals_description} were reportedly abandoned in {r.city}, {r.state}{r.incident_location?.includes("County") ? ` (${r.incident_location.split("—").pop().trim()})` : ""}.
            We are calling on rescue organizations, fosters and volunteers to contact Paws on Record and help.
          </p>
          <CampaignNotice rescue={r} />
          <CampaignHelpButtons rescue={r} disabled={draft} testPrefix="home-campaign" />
          <div className="flex flex-wrap items-center gap-4 pt-1">
            {!draft && <RescueShareButtons rescue={r} />}
            <Link to={`/urgent-rescues/${r.id}${draft ? "?preview=1" : ""}`} className="text-xs font-bold uppercase tracking-widest underline inline-flex items-center gap-1" data-testid="home-campaign-details-link">
              Full campaign details <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
